import { useAppRouteParams } from "@/AppRoutes";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { useSetEffect } from "@/utils/useSetEffect";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY_PREFIX = "appSideNav.closedCategoryIds";

const buildClosedCategoryIdsStorageKey = (
  uid: string,
  organizationId: string
): string => `${STORAGE_KEY_PREFIX}.${uid}.${organizationId}`;

const loadClosedCategoryIds = (
  uid: string,
  organizationId: string
): Set<string> => {
  try {
    const raw = localStorage.getItem(
      buildClosedCategoryIdsStorageKey(uid, organizationId)
    );
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

const saveClosedCategoryIds = (
  uid: string,
  organizationId: string,
  ids: string[]
): void => {
  // localStorage はストレージ無効環境（embedded webview 等）や容量超過時に
  // 例外を投げる。永続化は best-effort なので失敗は黙って捨てる。
  try {
    localStorage.setItem(
      buildClosedCategoryIdsStorageKey(uid, organizationId),
      JSON.stringify(ids)
    );
  } catch {
    // noop
  }
};

type CategoryState = { isClose: boolean; isShowAll: boolean };

const defaultCategoryState = (): CategoryState => ({
  isClose: false,
  isShowAll: false,
});

const buildCategoriesMap = (
  versionCategoryIds: string[],
  options?: {
    closedIds?: Set<string>;
    previousMap?: Map<string, CategoryState>;
  }
): Map<string, CategoryState> => {
  const next = new Map<string, CategoryState>();

  ["unknown", ...versionCategoryIds].forEach((id) => {
    const previousState = options?.previousMap?.get(id);
    next.set(id, {
      isClose: options?.closedIds
        ? options.closedIds.has(id)
        : previousState?.isClose ?? false,
      isShowAll: previousState?.isShowAll ?? false,
    });
  });

  return next;
};

const appSideNavGlobalContext = createContext<{
  categoriesMap: Map<string, CategoryState>;
  toggleIsCloseCategoryVersions: (name: string) => void;
  toggleShowAllCategoryVersions: (name: string) => void;
}>({} as never);

export const AppSideNavGlobalContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { versionCategories } = useVersionCategories();
  const { account } = useAppAccount();
  const { organizationId } = useAppRouteParams();
  const uid = account?.uid;
  const versionCategoryIds = useMemo(
    () => versionCategories?.map((category) => category.id) ?? [],
    [versionCategories]
  );

  const [categoriesMap, setCategoriesMap] = useState<
    Map<string, CategoryState>
  >(() => buildCategoriesMap(versionCategoryIds));

  useSetEffect(() => {
    setCategoriesMap((prev) =>
      buildCategoriesMap(versionCategoryIds, {
        closedIds:
          uid && organizationId
            ? loadClosedCategoryIds(uid, organizationId)
            : new Set<string>(),
        previousMap: prev,
      })
    );
  }, [uid, organizationId, versionCategoryIds]);

  const toggleIsCloseCategoryVersions = useCallback(
    (versionCategoryId: string) =>
      setCategoriesMap((prev) => {
        const next = new Map(prev);
        const cur = next.get(versionCategoryId) ?? defaultCategoryState();
        next.set(versionCategoryId, { ...cur, isClose: !cur.isClose });
        if (uid && organizationId) {
          const closed: string[] = [];
          next.forEach((v, k) => {
            if (v.isClose) closed.push(k);
          });
          saveClosedCategoryIds(uid, organizationId, closed);
        }
        return next;
      }),
    [uid, organizationId]
  );
  const toggleShowAllCategoryVersions = useCallback(
    (versionCategoryId: string) =>
      setCategoriesMap((prev) => {
        const next = new Map(prev);
        const cur = next.get(versionCategoryId) ?? defaultCategoryState();
        next.set(versionCategoryId, { ...cur, isShowAll: !cur.isShowAll });
        return next;
      }),
    []
  );

  const ctx = useMemo(
    () => ({
      categoriesMap,
      toggleIsCloseCategoryVersions,
      toggleShowAllCategoryVersions,
    }),
    [
      categoriesMap,
      toggleIsCloseCategoryVersions,
      toggleShowAllCategoryVersions,
    ]
  );

  return (
    <appSideNavGlobalContext.Provider value={ctx}>
      {children}
    </appSideNavGlobalContext.Provider>
  );
};

export const useAppSideNavGlobalContext = () => {
  return useContext(appSideNavGlobalContext);
};
